import { Group, Header, SimpleCell, Spinner, Text, Button } from '@vkontakte/vkui';
import { useNavigate } from 'react-router-dom';
import type { DashboardResponse } from '@pb26/shared';
import { useDashboard } from '../../api/hooks';
import { StatTile } from '../../components/admin/StatTile';
import { MiniBarChart } from '../../components/admin/MiniBarChart';

function pct(part: number, total: number) {
  if (total <= 0) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();
  const dashboard = data as DashboardResponse | undefined;

  if (isLoading) return <Spinner style={{ margin: 24 }} />;

  const s = dashboard?.summary;
  const ts = dashboard?.timeseries;
  const isAdmin = dashboard?.role === 'admin';

  return (
    <>
      {isAdmin && (
        <Group header={<Header size="s">Проверка UX</Header>}>
          <div style={{ padding: '0 16px 12px' }}>
            <Button
              size="l"
              stretched
              mode="secondary"
              onClick={() => navigate('/admin/user-flow')}
            >
              Путь участника
            </Button>
            <Text
              className="pb-text-secondary"
              style={{ fontSize: 13, marginTop: 8, display: 'block' }}
            >
              Повтор онбординга и экранов входа, как у нового пользователя
            </Text>
          </div>
        </Group>
      )}

      <Group header={<Header size="s">Участники</Header>}>
        <div className="pb-stat-grid">
          <StatTile
            label="Всего в базе"
            value={s?.participantsTotal ?? 0}
            accent="purple"
          />
          <StatTile
            label="Активных"
            value={s?.participantsActive ?? 0}
            hint={pct(s?.participantsActive ?? 0, s?.participantsTotal ?? 0)}
            accent="green"
          />
          <StatTile
            label="С привязкой VK"
            value={s?.participantsWithVk ?? 0}
            hint={pct(s?.participantsWithVk ?? 0, s?.participantsTotal ?? 0)}
            accent="blue"
          />
          <StatTile
            label="Ожидают входа"
            value={s?.participantsPreRegistered ?? 0}
            accent="amber"
          />
        </div>
      </Group>

      {(s?.participantsDisqualified ?? 0) > 0 && (
        <Group>
          <div className="pb-stat-grid pb-stat-grid--single">
            <StatTile
              label="Дисквалифицированы"
              value={s?.participantsDisqualified ?? 0}
              accent="amber"
            />
          </div>
        </Group>
      )}

      {isAdmin && (
        <Group header={<Header size="s">Лицензии</Header>}>
          <div className="pb-stat-grid">
            <StatTile
              label="Всего"
              value={s?.licensesTotal ?? 0}
              accent="purple"
            />
            <StatTile
              label="Выдано"
              value={s?.licensesAssigned ?? 0}
              hint={pct(s?.licensesAssigned ?? 0, s?.licensesTotal ?? 0)}
              accent="green"
            />
            <StatTile
              label="Свободно"
              value={s?.licensesAvailable ?? 0}
              hint={pct(s?.licensesAvailable ?? 0, s?.licensesTotal ?? 0)}
              accent="blue"
            />
          </div>
        </Group>
      )}

      {ts && (
        <Group header={<Header size="s">Динамика за 30 дней</Header>}>
          <div className="pb-chart-stack">
            <MiniBarChart
              title="Регистрации"
              subtitle="Новые участники по дате created_at"
              data={ts.registrations}
              color="#c084fc"
            />
            <MiniBarChart
              title="Активные входы"
              subtitle="Входы по last_login_at"
              data={ts.activations}
              color="#34d399"
            />
            <MiniBarChart
              title="Активность VK"
              subtitle="Входы участников с привязанным VK"
              data={ts.vkActivity}
              color="#60a5fa"
            />
            {isAdmin && (
              <MiniBarChart
                title="Выдача лицензий"
                subtitle="Назначения по assigned_at"
                data={ts.licenseAssignments}
                color="#f472b6"
              />
            )}
          </div>
        </Group>
      )}

      {dashboard?.recentImports && dashboard.recentImports.length > 0 && (
        <Group header={<Header size="s">Последние импорты</Header>} className="pb-glow-card">
          {dashboard.recentImports.map((row) => {
            const r = row as {
              id: number;
              type: string;
              filename: string;
              rowsAdded: number;
              createdAt: string;
            };
            return (
              <SimpleCell
                key={r.id}
                subtitle={`+${r.rowsAdded} · ${new Date(r.createdAt).toLocaleString('ru-RU')}`}
                multiline
              >
                {r.filename}
              </SimpleCell>
            );
          })}
        </Group>
      )}

      {!ts?.registrations.some((p) => p.value > 0) && (
        <Group>
          <Text className="pb-text-secondary" style={{ padding: '8px 16px', fontSize: 14 }}>
            Графики заполнятся по мере регистраций и входов участников.
          </Text>
        </Group>
      )}
    </>
  );
}
